import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { userimg } from '../assets/assets';

const OverlappingProfiles = ({ hellocians = [] }) => {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);

  // Normalise to a consistent shape regardless of whether data is populated
  const profiles = hellocians.map((h) => ({
    id:      h._id,
    image:   h.profilePicture || userimg,
    username: h.username || h._id,
    name:    `${h.firstName || ''}`.trim() || 'Hellocian',
    fullName: `${h.firstName || ''} ${h.lastName || ''}`.trim() || 'Hellocian',
    bio:     h.bio || '',
  }));

  const visibleProfiles = profiles.slice(0, 3);
  const hiddenProfiles  = profiles.slice(3);
  const hiddenCount     = hiddenProfiles.length;

  const openProfileModal  = (profile) => setSelectedProfile(profile);
  const closeProfileModal = ()         => setSelectedProfile(null);
  const openGroupModal    = ()         => setShowGroupModal(true);
  const closeGroupModal   = ()         => setShowGroupModal(false);

  if (profiles.length === 0) {
    return (
      <div className="flex items-center gap-2 py-1">
        <img src={userimg} alt="Hellocian" className="w-10 h-10 rounded-full opacity-40" />
        <p className="text-sm text-gray-400">No hellocian assigned</p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {/* Overlapping avatars */}
      <div className="flex items-center">
        {visibleProfiles.map((profile, index) => (
          <div
            key={profile.id}
            className={`relative cursor-pointer transition-transform hover:scale-110 ${index > 0 ? '-ml-3' : ''}`}
            style={{ zIndex: visibleProfiles.length - index }}
            onClick={() => openProfileModal(profile)}
            title={profile.name}
          >
            <img
              src={profile.image}
              alt={profile.name}
              className="md:w-10 w-6 md:h-10 h-6 rounded-full border-2 border-white shadow-md object-cover"
            />
          </div>
        ))}

        {/* +N overflow bubble */}
        {hiddenCount > 0 && (
          <div
            className="relative -ml-3 cursor-pointer transition-transform hover:scale-110"
            style={{ zIndex: 0 }}
            onClick={openGroupModal}
          >
            <div className="md:w-10 w-6 md:h-10 h-6 rounded-full bg-gray-200 border-2 border-white shadow-md flex items-center justify-center">
              <span className="md:text-xs text-[9px] md:font-semibold font-medium text-gray-600">+{hiddenCount}</span>
            </div>
          </div>
        )}
      </div>

      {/* Name(s) inline */}
      <div className="md:text-sm text-[10px]">
        <span className="font-medium md:font-semibold text-gray-800">
          {visibleProfiles.map(p => p.name).join(', ')}{hiddenCount > 0 ? ` +${hiddenCount} more` : ''}
        </span>
      </div>

   

      {/* ── Individual Profile Modal ── */}
      {selectedProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 relative shadow-2xl">
            <button
              onClick={closeProfileModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            <div className="text-center">
              <img
                src={selectedProfile.image}
                alt={selectedProfile.name}
                className="md:w-24 w-16 md:h-24 h-16 rounded-full mx-auto mb-4 object-cover shadow-lg"
              />
              <h3 className="text-[13px] font-medium md:text-xl md:font-semibold text-gray-800 mb-1">{selectedProfile.fullName}</h3>
              <p className="text-blue-500 text-[11px] md:text-sm mb-3">@{selectedProfile.username}</p>
              {selectedProfile.bio && (
                <p className="text-[11px] md:text-sm text-gray-500 italic mb-4 line-clamp-3">"{selectedProfile.bio}"</p>
              )}
          
            </div>
          </div>
        </div>
      )}

      {/* ── Group Modal for overflow profiles ── */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 relative shadow-2xl">
            <button
              onClick={closeGroupModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              More Hellocians ({hiddenCount})
            </h3>
            <div className="space-y-3">
              {hiddenProfiles.map((profile) => (
                <div
                  key={profile.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => { closeGroupModal(); openProfileModal(profile); }}
                >
                  <img
                    src={profile.image}
                    alt={profile.name}
                    className="w-12 h-12 rounded-full object-cover shadow-sm"
                  />
                  <div>
                    <p className="font-medium text-gray-800">{profile.name}</p>
                    <p className="text-sm text-blue-500">@{profile.username}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverlappingProfiles;